import { CommonRequest, Method, ServiceName, CommonResponse } from 'models/common/RestApi';
import { SetupProject } from 'models/setup/SetupProject';
import { callApi } from 'utils/ApiUtil';
import {
  SetupDailyReport,
  SetupDailyReportCondition,
  SetupPeople,
  WorkEquipment,
  WorkEquipmentWorker,
} from '../../models/setup/SetupDailyReport';
import { AxiosResponseHeaders } from 'axios';
import _ from 'lodash';

export const findDailyReportById = async (elmPrjId: string, excpId: string, crtnDt?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      excpId: excpId,
      crtnDt: crtnDt ?? '',
    }),
  };
  const response: CommonResponse<SetupDailyReport[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupDailyReport[];
};

/**
 * 작업일보 시작일 조회
 * @param elmPrjId
 * @param excpId
 */
export const findDailyReportStartDt = async (elmPrjId: string, excpId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report/start-dt`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      excpId: excpId,
    }),
  };
  const response: CommonResponse<string> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as string;
};

export const findDailyReportInfo = async (elmPrjId: string, excpId: string, crtnDt?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report-info`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      excpId: excpId,
      crtnDt: crtnDt ?? '',
    }),
  };
  const response: CommonResponse<SetupDailyReport[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupDailyReport[];
};

export const findLatestDailyReport = async (elmPrjId: string, excpId: string, crtnDt?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/latest-daily-report`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      elmPrjId: elmPrjId,
      excpId: excpId,
      crtnDt: crtnDt ?? '',
    }),
  };
  const response: CommonResponse<SetupDailyReport> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupDailyReport;
};

export const copyDailyReport = async (wkId?: string, prevWkId?: string, crtnDt?: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/daily-report/copy`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      wkId: wkId || '',
      prevWkId: prevWkId || '',
      crtnDt: crtnDt || '',
    },
  };
  const response: CommonResponse<SetupDailyReport> = await callApi(request);
  return response;
};

export const findPeopleInfo = async (
  wkId?: string,
  elmPrjId?: string,
  excpId?: string,
  crtnDt?: string
) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report/people`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      wkId: wkId ?? '',
      elmPrjId: elmPrjId ?? '',
      excpId: excpId ?? '',
      crtnDt: crtnDt ?? '',
    }),
  };
  const response: CommonResponse<SetupPeople> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupPeople;
};

export const findDailyReport = async (condition?: SetupDailyReportCondition) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      ...(condition || {}),
    }),
  };
  const response: CommonResponse<SetupDailyReport[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupDailyReport[];
};

export const findDailyReportPeople = async (wkId: string, excpId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report-people`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      wkId: wkId,
      excpId: excpId,
    }),
  };
  const response: CommonResponse<SetupPeople> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as SetupPeople;
};

export const saveDailyReport = async (masterData: SetupProject | undefined) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/daily-report`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: masterData,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

export const generateId = async (elmPrjId: string, excpId: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/daily-report/create-id`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { elmPrjId: elmPrjId, excpId: excpId },
  };

  const response: CommonResponse<string> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as string;
};

/**
 * 셋업일보 > 작업 설비 조회
 * @param wkId
 * @param elmPrjId
 * @param excpId
 */
export const findDailyReportEquipments = async (
  wkId?: string,
  elmPrjId?: string,
  excpId?: string
) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report/equipment`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      wkId: wkId ?? '',
      elmPrjId: elmPrjId ?? '',
      excpId: excpId ?? '',
    }),
  };
  const response: CommonResponse<WorkEquipment[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as WorkEquipment[];
};

/**
 * 작업설비 작업자 목록 조회
 * @param wkId
 * @param tgtEqpId
 */
export const findDailyReportEquipmentWorkers = async (wkId?: string, tgtEqpId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/daily-report/equipment/worker`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({
      wkId: wkId ?? '',
      tgtEqpId: tgtEqpId ?? '',
    }),
  };
  const response: CommonResponse<WorkEquipmentWorker[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : null) as WorkEquipmentWorker[];
};

/**
 * 작업설비 작업자 정보 저장
 * @param items
 */
export const saveDailyReportEquipmentWorkers = async (items: WorkEquipmentWorker[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/daily-report/equipment/worker`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };
  const response: CommonResponse<string> = await callApi(request);
  return response;
};

/**
 * 셋업일보 > 셋업일보 상세조회 엑셀 다운로드
 */
export const excelDownloadDailyReport = async (
  elmPrjId: string,
  excpId: string,
  crtnDt?: string
) => {
  try {
    const request: CommonRequest = {
      method: Method.POST,
      url: `/v1/setup/daily-report/download/excel`,
      serviceName: ServiceName.ELM_BE,
      responseType: 'blob',
      bodyParams: {
        elmPrjId: elmPrjId,
        excpId: excpId,
        crtnDt: crtnDt ?? '',
      },
    };
    const response: CommonResponse<Blob> = await callApi(request);

    if (response?.data) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      console.log('headers: ', response?.headers);
      const headers = response?.headers as AxiosResponseHeaders;
      const disposition = (headers['content-disposition'] as string) || '';

      if (!_.isEmpty(disposition)) {
        link.href = url;
        link.setAttribute(
          'download',
          String(decodeURIComponent(disposition?.split('filename=')[1]))
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert('Failed to download.');
      }
    }
  } catch (error) {
    console.log(error);
  }
};
